import { useClusterStore } from "@/store/cluster";
import { PublicKey } from "@solana/web3.js";
import { AccountAddress } from "@aptos-labs/ts-sdk";
import * as Yup from "yup";

export const passwordSchema = () => {
  return Yup.string()
    .min(4, "To have a minimum length of 5 characters")
    .max(50, "To have a maximum length of 50 characters");
};

export const usernameSchema = () => {
  return Yup.string()
    .min(5, "To have a minimum length of 5 characters")
    .max(30, "To have a maximum length of 30 characters");
};

export const emailSchema = () => {
  return Yup.string().email("To be a valid email");
};

export const codeSchema = () => {
  return Yup.string()
    .min(8, "To have a minimum length of 8 characters")
    .max(8, "To have a maximum length of 8 characters");
};

export const phoneSchema = () => {
  return Yup.string()
    .min(8, "To have a minimum length of 8 characters")
    .max(15, "To have a maximum length of 15 characters");
};

export const stringSchema = () => {
  return Yup.string().min(2, "To have a minimum length of 2 characters");
};

// e.g. 4vJ9JU1bJJE96FWSJKvHsmmFADCg4gpZQff4P3bkLKi
export const solanaWalletAddressSchema = () => {
  return stringSchema().test({
    test: (value, ctx) => {
      if (typeof value !== "string") {
        return true;
      }

      if (value.length === 0) {
        return true;
      }

      const error = ctx.createError({
        message: "wallet ID not recognized - please try again"
      });

      try {
        const publicKey = new PublicKey(value);

        if (PublicKey.isOnCurve(publicKey)) {
          return true;
        }

        return error;
      } catch (e) {
        return error;
      }
    }
  });
};

export const aptosWalletAddressSchema = () => {
  return stringSchema().test({
    test: (value, ctx) => {
      if (typeof value !== "string") {
        return true;
      }

      if (value.length === 0) {
        return true;
      }

      const error = ctx.createError({
        message: "wallet ID not recognized - please try again"
      });

      try {
        const result = AccountAddress.isValid({ input: value, strict: true });

        if (result.valid) {
          return true;
        }

        return error;
      } catch (e) {
        console.log(e);
        return error;
      }
    }
  });
};

let debouncedInterval: number | null = null;

export const clusterNameSchema = () => {
  const cache: Record<string, boolean> = {};

  let abortController: AbortController | undefined;

  return Yup.string()
    .min(1, "To have a minimum length of 1 characters")
    .test("Unique name", "Name already in use", function (value) {
      if (typeof value !== "string") {
        return false;
      }
      if (value in cache) {
        return cache[value];
      }

      if (debouncedInterval) {
        clearTimeout(debouncedInterval);
      }

      if (abortController) {
        abortController.abort();
      }
      abortController = new AbortController();

      return new Promise((resolve) => {
        debouncedInterval = window.setTimeout(() => {
          useClusterStore
            .getState()
            .checkIfNameIsAvailable(value as string, abortController)
            .then((result) => {
              cache[value] = result;
              resolve(result);
            })
            .catch((e) => {
              if (e.message === "canceled") {
                resolve(true);
                return;
              }

              console.log(e.message);
              resolve(false);
            });
        }, 500);
      });
    });
};

export const REQUIRED_MESSAGE = "Required";
